// src/components/StaffAppraisals.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  AlertTitle,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Fade,
  Grow,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Grade as GradeIcon,
  FileUpload as FileUploadIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Image as ImageIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';
import TenurePopupDialog from '../../../../components/TenurePopupDialog';

const statusColors = {
  pending: { bg: '#fff3e0', text: '#ff9800', label: 'Pending Review' },
  in_progress: { bg: '#e3f2fd', text: '#2196f3', label: 'In Progress' },
  completed: { bg: '#e8f5e9', text: '#4caf50', label: 'Completed' },
  reviewed: { bg: '#f3e5f5', text: '#9c27b0', label: 'Reviewed' },
  approved: { bg: '#e0f7fa', text: '#00bcd4', label: 'Approved' },
  rejected: { bg: '#ffebee', text: '#f44336', label: 'Rejected' },
};

const StaffAppraisals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tenureDialogOpen, setTenureDialogOpen] = useState(false);
  
  const [appraisals, setAppraisals] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0,
    reviewed: 0,
    approved: 0,
    rejected: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [departments, setDepartments] = useState([]);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    id: null,
    appraisal_status: '',
    comment: '',
    upload_path: '',
  });
  const [uploading, setUploading] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Helper function to safely format numbers
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Helper function to safely calculate percentage
  const safePercentage = (part, total) => {
    if (!total || total === 0) return 0;
    const percent = (safeNumber(part) / safeNumber(total)) * 100;
    return isNaN(percent) ? 0 : Math.round(percent);
  };

  // Helper function to get file download URL
  const getFileDownloadUrl = (filename) => {
    if (!filename) return '#';
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    if (filename.startsWith('http') || filename.startsWith('/api/')) {
      return filename;
    }
    return `${baseUrl}/hotel/HR/download_appraisal_file.php?path=${encodeURIComponent(filename)}`;
  };

  // Helper function to get file icon based on extension
  const getFileIcon = (filename) => {
    if (!filename) return <FileUploadIcon />;
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <PdfIcon />;
      case 'doc':
      case 'docx': return <DocIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <ImageIcon />;
      default: return <FileUploadIcon />;
    }
  };

  // Fetch appraisals with debounce
  const fetchAppraisals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const url = new URL(`${baseUrl}/hotel/HR/get_appraisals.php`);
      url.searchParams.append('limit', rowsPerPage);
      url.searchParams.append('offset', page * rowsPerPage);
      if (statusFilter !== 'all') url.searchParams.append('status', statusFilter);
      if (departmentFilter !== 'all') url.searchParams.append('department', departmentFilter);
      if (searchTerm) url.searchParams.append('search', searchTerm);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setAppraisals(result.data || []);
        setStats({
          total: safeNumber(result.stats?.total),
          pending: safeNumber(result.stats?.pending),
          in_progress: safeNumber(result.stats?.in_progress),
          completed: safeNumber(result.stats?.completed),
          reviewed: safeNumber(result.stats?.reviewed),
          approved: safeNumber(result.stats?.approved),
          rejected: safeNumber(result.stats?.rejected),
        });
        setTotalRecords(result.pagination?.total || 0);
      } else {
        setError(result.message || 'Failed to load appraisals');
      }
    } catch (err) {
      console.error('Error fetching appraisals:', err);
      setError('Failed to load appraisals. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, departmentFilter, searchTerm]);

  const fetchDepartments = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/get_depts_list.php`);
      const result = await response.json();
      if (result.success) {
        setDepartments(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  }, []);

  useEffect(() => {
    fetchAppraisals();
    fetchDepartments();
  }, [fetchAppraisals, fetchDepartments]);

  const handleUpdateStatus = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/get_appraisals.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Appraisal updated successfully', severity: 'success' });
        setEditDialogOpen(false);
        fetchAppraisals();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to update appraisal', severity: 'error' });
      }
    } catch (err) {
      console.error('Update error:', err);
      setSnackbar({ open: true, message: 'Failed to update appraisal', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!selectedAppraisal?.id) return;
    
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/get_appraisals.php?id=${selectedAppraisal.id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Appraisal deleted successfully', severity: 'success' });
        setDeleteDialogOpen(false);
        setSelectedAppraisal(null);
        fetchAppraisals();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to delete appraisal', severity: 'error' });
      }
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({ open: true, message: 'Failed to delete appraisal', severity: 'error' });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File size exceeds 5MB limit', severity: 'error' });
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appraisal_id', editForm.id);
    
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/upload_appraisal_file.php`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      
      if (result.success) {
        setEditForm(prev => ({ ...prev, upload_path: result.file_path }));
        setSnackbar({ open: true, message: 'File uploaded successfully', severity: 'success' });
        fetchAppraisals();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to upload file', severity: 'error' });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar({ open: true, message: 'Failed to upload file', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const exportToCSV = () => {
    if (!appraisals.length) {
      setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
      return;
    }
    
    const headers = ['Full Name', 'Department', 'Phone', 'Email', 'Status', 'Comment', 'Date Created', 'Date Updated'];
    const csvData = appraisals.map(a => [
      `"${(a.full_name || '').replace(/"/g, '""')}"`,
      `"${(a.department || 'N/A').replace(/"/g, '""')}"`,
      `"${(a.phone || '').replace(/"/g, '""')}"`,
      `"${(a.email || '').replace(/"/g, '""')}"`,
      `"${(a.status_display || 'Unknown').replace(/"/g, '""')}"`,
      `"${(a.comment || '').replace(/"/g, '""')}"`,
      a.date_created ? new Date(a.date_created).toLocaleDateString() : '',
      a.date_updated ? new Date(a.date_updated).toLocaleDateString() : '',
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff_appraisals_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPieChartData = () => {
    const chartData = [
      { name: 'Pending', value: safeNumber(stats?.pending), color: '#ff9800' },
      { name: 'In Progress', value: safeNumber(stats?.in_progress), color: '#2196f3' },
      { name: 'Completed', value: safeNumber(stats?.completed), color: '#4caf50' },
      { name: 'Reviewed', value: safeNumber(stats?.reviewed), color: '#9c27b0' },
      { name: 'Approved', value: safeNumber(stats?.approved), color: '#00bcd4' },
      { name: 'Rejected', value: safeNumber(stats?.rejected), color: '#f44336' },
    ].filter(item => item.value > 0);
    
    // If no data, show a placeholder
    if (chartData.length === 0) {
      return [{ name: 'No Data', value: 1, color: '#e0e0e0' }];
    }
    
    return chartData;
  };

  const StatCard = ({ title, value, color, icon: Icon, onClick, loading: cardLoading }) => (
    <Grow in timeout={300}>
      <Card 
        sx={{ 
          borderRadius: 3, 
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 6 } : {},
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="textSecondary">{title}</Typography>
              {cardLoading ? (
                <Skeleton width={60} height={40} />
              ) : (
                <Typography variant="h4" fontWeight="bold">{safeNumber(value)}</Typography>
              )}
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
              <Icon />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );

  // Loading state
  if (loading && appraisals.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading appraisals...</Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error && !appraisals.length) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={fetchAppraisals}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
          height: '100vh',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => setSnackbar({ open: true, message: 'You have new notifications!', severity: 'info' })}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          {/* Staff Tenure Milestone Banner */}
          <Alert 
            severity="info" 
            icon={<TrophyIcon />}
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: '#e3f2fd',
              borderLeft: '4px solid #1976d2',
              '&:hover': {
                backgroundColor: '#bbdefb',
              }
            }}
            onClick={() => setTenureDialogOpen(true)}
            action={
              <Button color="primary" size="small" variant="outlined" sx={{ borderRadius: 2 }}>
                View Details
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrophyIcon sx={{ fontSize: 20 }} /> Staff Tenure Milestone
            </AlertTitle>
            Click here to view all staff members who have been with Eleganté for 3+ months.
          </Alert>

          {/* Header */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3, 
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Staff Appraisals
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Manage and track staff performance appraisals
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                startIcon={<DownloadIcon />}
                onClick={exportToCSV}
                disabled={appraisals.length === 0}
                sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                Export Report
              </Button>
            </Box>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="Total" value={stats?.total} color="#1976d2" icon={AssessmentIcon} loading={loading} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="Pending" value={stats?.pending} color="#ff9800" icon={PendingIcon} onClick={() => setStatusFilter('pending')} loading={loading} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="In Progress" value={stats?.in_progress} color="#2196f3" icon={TimelineIcon} onClick={() => setStatusFilter('in_progress')} loading={loading} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="Completed" value={stats?.completed} color="#4caf50" icon={CheckCircleIcon} onClick={() => setStatusFilter('completed')} loading={loading} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="Reviewed" value={stats?.reviewed} color="#9c27b0" icon={VisibilityIcon} onClick={() => setStatusFilter('reviewed')} loading={loading} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard title="Approved" value={stats?.approved} color="#00bcd4" icon={GradeIcon} onClick={() => setStatusFilter('approved')} loading={loading} />
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>Appraisal Status Distribution</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={getPieChartData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => {
                        if (name === 'No Data') return 'No Data';
                        const percentage = percent ? (percent * 100).toFixed(0) : 0;
                        return `${name} ${percentage}%`;
                      }}
                      labelLine={false}
                    >
                      {getPieChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => safeNumber(value)} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>Quick Stats</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Pending Approval Rate</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {safePercentage(stats?.pending, stats?.total)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={safePercentage(stats?.pending, stats?.total)} 
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#ffe0b2' }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Completion Rate</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {safePercentage(stats?.completed, stats?.total)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={safePercentage(stats?.completed, stats?.total)} 
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#c8e6c9' }}
                    color="success"
                  />
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Active Departments</Typography>
                    <Typography variant="body2" fontWeight="bold">{safeNumber(departments.length)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Appraisals</Typography>
                    <Typography variant="body2" fontWeight="bold">{safeNumber(stats?.total)}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 2, minWidth: 200 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Department</InputLabel>
                <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} label="Department">
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.departments}>{dept.departments}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAppraisals}>
                Refresh
              </Button>
            </Box>
          </Paper>

          {/* Appraisals Table */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell>Staff</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Hire Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    // Loading skeletons
                    Array.from(new Array(rowsPerPage)).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        <TableCell><Skeleton variant="circular" width={40} height={40} /><Skeleton width={120} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                        <TableCell><Skeleton width={150} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                      </TableRow>
                    ))
                  ) : appraisals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1" color="textSecondary">No appraisal records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    appraisals.map((appraisal) => (
                      <TableRow key={appraisal.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: statusColors[appraisal.appraisal_status]?.bg || '#e0e0e0', color: statusColors[appraisal.appraisal_status]?.text || '#757575' }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{appraisal.full_name || 'N/A'}</Typography>
                              <Typography variant="caption" color="textSecondary">{appraisal.position || 'Staff'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" icon={<BusinessIcon />} label={appraisal.department || 'N/A'} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{appraisal.phone || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">{appraisal.email || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 14, color: 'textSecondary' }} />
                            <Typography variant="body2">{appraisal.hire_date || 'N/A'}</Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {safeNumber(appraisal.months_employed)} months tenure
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={statusColors[appraisal.appraisal_status]?.label || appraisal.status_display || 'Unknown'}
                            size="small"
                            sx={{ 
                              bgcolor: statusColors[appraisal.appraisal_status]?.bg || '#e0e0e0',
                              color: statusColors[appraisal.appraisal_status]?.text || '#757575',
                              fontWeight: 'medium',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {appraisal.date_updated ? new Date(appraisal.date_updated).toLocaleDateString() : 
                             appraisal.date_created ? new Date(appraisal.date_created).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => { setSelectedAppraisal(appraisal); setViewDialogOpen(true); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Status">
                            <IconButton size="small" onClick={() => { 
                              setSelectedAppraisal(appraisal); 
                              setEditForm({ 
                                id: appraisal.id, 
                                appraisal_status: appraisal.appraisal_status, 
                                comment: appraisal.comment || '', 
                                upload_path: appraisal.upload_path || '' 
                              }); 
                              setEditDialogOpen(true); 
                            }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => { setSelectedAppraisal(appraisal); setDeleteDialogOpen(true); }} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
            <Typography variant="body2" color="textSecondary">
              Designed by Elegantè IT
            </Typography>
          </footer>
        </Box>
      </Box>

      {/* Staff Tenure Popup Dialog */}
      <TenurePopupDialog 
        open={tenureDialogOpen}
        onClose={() => setTenureDialogOpen(false)}
        minMonths={3}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Appraisal Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.appraisal_status}
                onChange={(e) => setEditForm({ ...editForm, appraisal_status: e.target.value })}
                label="Status"
              >
                <MenuItem value="pending">Pending Review</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Comment"
              multiline
              rows={4}
              value={editForm.comment || ''}
              onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
              fullWidth
              placeholder="Add your comments about this appraisal..."
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              disabled={uploading}
            >
              Upload Document
              <input type="file" hidden accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleFileUpload} />
            </Button>
            {editForm.upload_path && (
              <Alert 
                severity="info" 
                icon={getFileIcon(editForm.upload_path)}
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    href={getFileDownloadUrl(editForm.upload_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </Button>
                }
              >
                File uploaded: {editForm.upload_path.split('/').pop()}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus} startIcon={<SaveIcon />}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedAppraisal && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Appraisal Details</Typography>
                <Chip 
                  label={statusColors[selectedAppraisal.appraisal_status]?.label || selectedAppraisal.status_display}
                  sx={{ bgcolor: statusColors[selectedAppraisal.appraisal_status]?.bg || '#e0e0e0', 
                         color: statusColors[selectedAppraisal.appraisal_status]?.text || '#757575' }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Staff Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ width: 56, height: 56, bgcolor: theme.palette.primary.main }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{selectedAppraisal.full_name || 'N/A'}</Typography>
                          <Typography variant="body2" color="textSecondary">{selectedAppraisal.department || 'N/A'}</Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'textSecondary' }} />
                        <Typography variant="body2">{selectedAppraisal.phone || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <EmailIcon sx={{ fontSize: 16, color: 'textSecondary' }} />
                        <Typography variant="body2">{selectedAppraisal.email || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'textSecondary' }} />
                        <Typography variant="body2">
                          Hired: {selectedAppraisal.hire_date || 'N/A'} 
                          {selectedAppraisal.months_employed ? ` (${safeNumber(selectedAppraisal.months_employed)} months ago)` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Appraisal Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Created:</Typography>
                        <Typography variant="body2">
                          {selectedAppraisal.date_created ? new Date(selectedAppraisal.date_created).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Last Updated:</Typography>
                        <Typography variant="body2">
                          {selectedAppraisal.date_updated ? new Date(selectedAppraisal.date_updated).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                      {selectedAppraisal.comment && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" gutterBottom>Comments:</Typography>
                          <Typography variant="body2">{selectedAppraisal.comment}</Typography>
                        </>
                      )}
                      {selectedAppraisal.upload_path && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Button 
                            startIcon={getFileIcon(selectedAppraisal.upload_path)} 
                            size="small" 
                            variant="outlined"
                            fullWidth
                            href={getFileDownloadUrl(selectedAppraisal.upload_path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1 }}
                          >
                            View Uploaded Document
                          </Button>
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  setViewDialogOpen(false);
                  setEditForm({ 
                    id: selectedAppraisal.id, 
                    appraisal_status: selectedAppraisal.appraisal_status, 
                    comment: selectedAppraisal.comment || '',
                    upload_path: selectedAppraisal.upload_path || ''
                  });
                  setEditDialogOpen(true);
                }}
              >
                Update Status
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the appraisal record for <strong>{selectedAppraisal?.full_name}</strong>?</Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffAppraisals;